import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faChevronRight, faSearch, faTag } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { classNames } from "@/utils/classNames";

import { normalizeVariableSearch, VariableRow } from "../utils/variableRows";

import { VariableSectionRow } from "./VariableSectionRow";

interface VariablesSectionCardProps {
	rows: VariableRow[];
	inUseCount: number;
	onRowInsert: (row: VariableRow) => void;
	/** Icon for the header badge and each row — defaults to the generic tag. */
	icon?: IconDefinition;
	/** Tailwind classes for the header/row icon badge (bg + text color). */
	iconClassName?: string;
	/** Translation key for the card title. */
	titleKey?: string;
	/** Translation key for the search input placeholder. */
	searchPlaceholderKey?: string;
	/** Translation key for the empty-search message. */
	emptySearchKey?: string;
}

const DEFAULT_ICON_CLASS = "bg-background-light text-text-meta";

/**
 * Collapsible "Variables" section card: header with in-use count, body with a
 * search input and one row per placeholder available for the current lead.
 */
export const VariablesSectionCard: FunctionComponent<VariablesSectionCardProps> = ({
	rows,
	inUseCount,
	onRowInsert,
	icon = faTag,
	iconClassName = DEFAULT_ICON_CLASS,
	titleKey = "variables.section.title",
	searchPlaceholderKey = "variables.section.searchPlaceholder",
	emptySearchKey = "variables.section.emptySearch",
}) => {
	const { t } = useTranslation();

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [search, setSearch] = useState<string>("");

	const filteredRows = useMemo(() => {
		const query = normalizeVariableSearch(search);

		if (!query) {
			return rows;
		}

		return rows.filter(
			(row) =>
				normalizeVariableSearch(row.label).includes(query) ||
				normalizeVariableSearch(row.sampleValue).includes(query)
		);
	}, [rows, search]);

	return (
		<div className="w-full border border-border-light rounded-lg bg-white overflow-hidden">
			<div
				className={classNames(
					"flex items-center justify-between gap-2 px-3 py-2.5 bg-background-light/60 cursor-pointer select-none",
					isOpen && "border-b border-border-light"
				)}
				onClick={() => setIsOpen((open) => !open)}
			>
				<div className="flex items-center gap-2 min-w-0 flex-1">
					<Icon
						className={classNames(
							"w-3.5 h-3.5 flex-shrink-0 text-text-meta transition-transform",
							isOpen && "rotate-90"
						)}
						icon={faChevronRight}
					/>
					<span
						className={classNames(
							"w-[22px] h-[22px] flex-shrink-0 rounded flex items-center justify-center",
							iconClassName
						)}
					>
						<Icon className="w-3 h-3" icon={icon} />
					</span>
					<span className="text-xs font-semibold text-text whitespace-nowrap">{t(titleKey)}</span>
					<span className="text-xxs font-medium px-2 py-0.5 bg-white border border-border-light rounded-full text-text-meta whitespace-nowrap">
						{t("variables.section.inUse", { count: inUseCount })}
					</span>
				</div>
			</div>

			{isOpen && (
				<div className="flex flex-col overflow-hidden">
					<div className="relative p-2 border-b border-border-light bg-white flex-shrink-0">
						<Icon
							className="absolute left-[18px] top-1/2 -translate-y-1/2 w-3 h-3 text-text-meta"
							icon={faSearch}
						/>
						<input
							className="w-full pl-7 pr-2.5 py-1.5 border border-border-light rounded-md text-xs bg-background-light focus:outline-none focus:bg-white focus:border-blue-action"
							placeholder={t(searchPlaceholderKey)}
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
					</div>

					<div className="overflow-y-auto max-h-[280px] p-2 flex flex-col gap-1">
						{filteredRows.length === 0 && (
							<div className="px-2 py-2 text-xs text-text-meta italic">{t(emptySearchKey)}</div>
						)}

						{filteredRows.map((row) => (
							<VariableSectionRow
								key={row.key}
								icon={icon}
								iconClassName={iconClassName}
								row={row}
								onInsert={onRowInsert}
							/>
						))}
					</div>
				</div>
			)}
		</div>
	);
};
